import Box from '../components/Box';
import Order from '../components/Order';
import Form from '../components/Form';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  changePasswordCompletionMessage,
  changePasswordPolicyMessage,
  matchErrorMessage,
} from '../../../message';
import FormBlockItem from '../../../components/common/FormBlockItem';
import { usePutUserPasswordMutation } from '../../../api/userApi';
import HeaderLayout from '../../../components/HeaderLayout';
import { createPasswordRegisterOptions } from '../../../validators/password';
import { logger } from '../../../services/logger';
import { isFetchBaseQueryError } from '../../../services/isFetchBaseQueryError';

const currentPasswordRegisterOptions =
  createPasswordRegisterOptions('現在のパスワード');
const newPasswordRegisterOptions =
  createPasswordRegisterOptions('新しいパスワード');
const newPasswordConfirmationRegisterOptions =
  createPasswordRegisterOptions('新しいパスワード（確認）');

function ChangePassword() {
  const { register, errors, getValues, onSubmit, onCancel } = useFormProps();

  return (
    <HeaderLayout title="マイページ">
      <Form onSubmit={onSubmit} onCancel={onCancel}>
        <Box title="パスワードを変更" isChange={true}>
          <Order>現在のパスワードと新しいパスワードを入力してください。</Order>
          <FormBlockItem
            label="現在のパスワード"
            type="password"
            autoComplete="current-password"
            autoFocus
            {...register('currentPassword', currentPasswordRegisterOptions)}
            error={
              errors['currentPassword']?.message !== undefined
                ? String(errors['currentPassword']?.message)
                : undefined
            }
          />
          <FormBlockItem
            label="新しいパスワード"
            type="password"
            text={changePasswordPolicyMessage}
            autoComplete="new-password"
            {...register('newPassword', {
              ...newPasswordRegisterOptions,
              deps: ['confirmNewPassword'],
            })}
            error={
              errors['newPassword']?.message
                ? String(errors['newPassword']?.message)
                : undefined
            }
          />
          <FormBlockItem
            label="新しいパスワード（確認）"
            type="password"
            autoComplete="new-password"
            {...register('confirmNewPassword', {
              ...newPasswordConfirmationRegisterOptions,
              validate: (value) => {
                const base =
                  newPasswordConfirmationRegisterOptions.validate(value);
                if (base !== true) return base;
                if (value !== getValues('newPassword'))
                  return matchErrorMessage;
                return true;
              },
            })}
            error={
              errors['confirmNewPassword']?.message
                ? String(errors['confirmNewPassword']?.message)
                : undefined
            }
          />
        </Box>
      </Form>
    </HeaderLayout>
  );
}

export default ChangePassword;

function useFormProps() {
  const navigate = useNavigate();
  const [putUserPassword] = usePutUserPasswordMutation();
  const methods = useForm({ mode: 'onBlur' });

  const onSubmit = methods.handleSubmit(async (data) => {
    await putUserPassword({
      currentPassword: data.currentPassword ?? '',
      newPassword: data.newPassword ?? '',
      confirmNewPassword: data.confirmNewPassword ?? '',
    })
      .unwrap()
      .then(() => {
        // マイページ画面に成功メッセージ付きでリダイレクトする
        navigate(
          `/mypage?success=${encodeURIComponent(
            changePasswordCompletionMessage
          )}`
        );
      })
      .catch((e) => {
        // 現在のパスワードの入力項目をエラー表記にするためエラーメッセージ未設定でエラーを設定
        if (isFetchBaseQueryError(e) && e.status === 400) {
          methods.setError('currentPassword', { message: '' });
        }
        logger.error(e);
      });
  });

  const onCancel = useCallback(() => {
    navigate('..');
  }, []);

  return {
    errors: methods.formState.errors,
    ...methods,
    onSubmit,
    onCancel,
  };
}
